import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/config';

export default function Cart() {
  const { cart, loading, updateQuantity, removeFromCart, getCartTotal, sessionId } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');

  const handleCheckout = async () => {
    if (!customerEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setCheckoutLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout`, {
        origin_url: originUrl,
        session_id: sessionId,
        customer_email: customerEmail
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <ShoppingBag className="h-20 w-20 text-muted-foreground mb-6" />
        <h1 className="font-heading font-bold text-2xl text-pockett-gray mb-2">
          Your cart is empty
        </h1>
        <p className="text-muted-foreground mb-8 text-center">
          Browse our handmade treasures and find something special!
        </p>
        <Button 
          asChild
          className="bg-pockett-purple hover:bg-pockett-purple/90 rounded-full px-8"
          data-testid="continue-shopping-btn"
        >
          <Link to="/shop">Continue Shopping</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Back Button */}
        <Link 
          to="/shop"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-pockett-purple transition-colors mb-8"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="h-4 w-4" />
          Continue Shopping
        </Link>

        <h1 className="font-heading font-bold text-3xl md:text-4xl text-pockett-gray mb-8">
          Your Cart
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, index) => (
              <div 
                key={`${item.product_id}-${item.variant_id || 'default'}-${index}`}
                className="flex gap-4 p-4 bg-white rounded-2xl border border-border/50 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`cart-item-${item.product_id}`}
              >
                <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                  <img 
                    src={item.image_url} 
                    alt={item.product_name}
                    className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-xl"
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link 
                    to={`/product/${item.product_id}`}
                    className="font-heading font-semibold text-pockett-gray hover:text-pockett-purple transition-colors"
                  >
                    {item.product_name}
                  </Link>
                  {item.variant_name && (
                    <p className="text-sm text-muted-foreground mt-1">{item.variant_name}</p>
                  )}
                  <p className="font-semibold text-pockett-purple mt-2">
                    ${item.price.toFixed(2)} each
                  </p>
                  
                  {/* Quantity Controls */}
                  <div className="flex items-center gap-3 mt-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateQuantity(item.product_id, item.variant_id, item.quantity - 1)}
                        disabled={loading || item.quantity <= 1}
                        data-testid={`decrease-qty-${item.product_id}`}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateQuantity(item.product_id, item.variant_id, item.quantity + 1)}
                        disabled={loading}
                        data-testid={`increase-qty-${item.product_id}`}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => removeFromCart(item.product_id, item.variant_id)}
                      disabled={loading}
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-heading font-bold text-lg text-pockett-gray">
                    ${(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-border/50 p-6 sticky top-24">
              <h2 className="font-heading font-semibold text-xl text-pockett-gray mb-6">
                Order Summary
              </h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span>${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t border-border pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="font-semibold text-pockett-gray">Total</span>
                  <span className="font-heading font-bold text-xl text-pockett-purple">
                    ${getCartTotal().toFixed(2)} AUD
                  </span>
                </div>
              </div>

              {/* Customer Email */}
              <div className="mb-4 space-y-2">
                <Label htmlFor="customer-email">Email for order confirmation</Label>
                <Input
                  id="customer-email"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="customer-email-input"
                />
              </div>

              <Button 
                className="w-full bg-pockett-purple hover:bg-pockett-purple/90 rounded-full h-12 font-semibold"
                onClick={handleCheckout}
                disabled={checkoutLoading || loading}
                data-testid="checkout-btn"
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Proceed to Checkout'
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
